import {
  IApiBusinessProfileProto,
  IClientRelationshipDetailsProto,
  PurchasedSingleClassCreditProto,
  space
} from "../../compiled";
import {priceToString} from "../../util/PriceUtil";
import {PurchaseOption, SelectedBooking} from "./BookingFlow";
import BillingPeriodUnitProto = space.kenko.proto.BillingPeriodUnitProto;
import IBillingPeriodProto = space.kenko.proto.IBillingPeriodProto;
import {shortDateFormat, shortDateFormat2} from "../../util/DateTimeUtil";
import BusinessProfileProto = space.kenko.proto.BusinessProfileProto;
import {findClassTemplate, findPriceCategory, findService} from "./BusinessUtil";
import PaymentPolicyProto = space.kenko.proto.PaymentPolicyProto;
import IPaymentPolicyProto = space.kenko.proto.IPaymentPolicyProto;

type Props = {
  business: IApiBusinessProfileProto,
  selectedBooking: SelectedBooking,
  clientRelationship: IClientRelationshipDetailsProto | null | undefined,
  onSelected: (option: PurchaseOption) => void;
}

/**
 * Displays which purchase options are available. The client relatinoship which contains
 * this stuff has been pre-fetched in the get_booking_info request
 */
export const SelectPurchaseOption = (props: Props) => {

  let paymentPolicy: IPaymentPolicyProto | null | undefined;
  let classOrServiceName: string | null | undefined;
  let classOrServiceId: string | null | undefined;

  if (props.selectedBooking.class) {
    paymentPolicy = props.selectedBooking.class.paymentPolicy;
    classOrServiceName = props.selectedBooking.class.className;
    classOrServiceId = props.selectedBooking.class.classTemplateId;

  } else if (props.selectedBooking.appointment) {
    paymentPolicy = props.selectedBooking.appointment.service.paymentPolicy;
    classOrServiceName = props.selectedBooking.appointment.service.name;
    classOrServiceId = props.selectedBooking.appointment.service.sku;
  }

  // If there's a class template id for this class (new type of class) or it's a service,
  const bookingHasTemplateId = !!classOrServiceId;

  const priceCategoryId = paymentPolicy?.prepaid?.priceCategoryId;
  const priceCategory = findPriceCategory(props.business, priceCategoryId);

  if (!classOrServiceId) {

    // This case only happens if there is no class template for a class, which means it's an
    // old style class that is not using a template. Services always use templates so this is
    // always set for services
    if (!priceCategoryId) {
      return <div className={'ErrorText'}>Error booking, code 152</div>
    }

    // This is the price category on the business profile, it contains the display name
    // and single credit price.
    if (!priceCategory) {
      return <div className={'ErrorText'}>Error booking, code 153</div>
    }
  }


  return <div style={{display: 'flex', flexDirection: "column", textAlign: 'start'}}>
    <h3>Select Purchase Option</h3>

    {/* Use an existing class credit */}
    {props.clientRelationship?.singleClassCredits?.filter(credit => !credit.used)
        .filter(credit => {
          switch ((credit as PurchasedSingleClassCreditProto).CreditType) {
            case "classDetails":
              // This is a new type of class credit, it works with a specific class/service.
              return credit.classDetails?.classTemplateId == classOrServiceId;
            case "priceCategoryDetails":
              // This is the old type of class credit, it works with a price category. But we also let
              // these be used with new style events for backwards compatibility. The prices have to 
              // match for it to be allowed.
              if (bookingHasTemplateId) {
                return credit.priceCategoryDetails?.singleClassPrice?.amount == paymentPolicy?.prepaid?.price?.amount
              } else {
                return credit.priceCategoryDetails?.categoryId == priceCategoryId;
              }
            default:
              return false;
          }
        })
        .map(credit => {
          return <button key={'cc-' + credit.classCreditId} className={'BookingOverlaySelectable'} onClick={() => {
            props.onSelected({useExistingClassCredit: credit});
          }}>
            <div className={'BookingOverlayButtonContent'}>
              <div className='VerticalFlexBox Flex1'>
                <div><b>Single Class Credit</b></div>
                <div>{credit.classDetails?.className || credit.serviceDetails?.serviceName || credit.priceCategoryDetails?.displayName}</div>
                <div style={{color: "#baa"}}>Purchased: {shortDateFormat2(credit.purchaseTimestamp)}</div>
              </div>
              <div><b>FREE</b></div>
            </div>
          </button>
        })}

    {classOrServiceId && props.clientRelationship?.purchasedClassPacks?.filter(
        pack => (pack.packageDetails?.includedClassOrServiceIds?.indexOf(classOrServiceId as string) ?? -1) >= 0)
        .filter(pack => pack.classesRemaining && pack.classesRemaining > 0)
        .map(pack => {
          return <button key={'cp-' + pack.purchasedClassPackId} className={'BookingOverlaySelectable'} onClick={() => {
            props.onSelected({useExistingClassPackage: pack});
          }}>
            <div className={'BookingOverlayButtonContent'}>
              <div className='VerticalFlexBox Flex1'>
                <div><b>{pack.packageDetails?.displayName}</b></div>
                <div>{pack.classesRemaining} classes remaining</div>
                <div style={{color: "#baa"}}>Purchased: {shortDateFormat2(pack.purchaseTimestamp)}</div>
              </div>
              <div><b>FREE</b></div>
            </div>
          </button>
        })}

    {/*
     Buy a single class drop-in. The legacy buy new price category is only available 
     if the class does not have a class template yet. 
    */}
    {!bookingHasTemplateId && <button className={'BookingOverlaySelectable'} onClick={() => {
      props.onSelected({buySingleCredit: {priceCategory: priceCategory}});
    }}>
      <div className={'BookingOverlayButtonContent'}>
        <div className={'Flex1'}><b>Single '{priceCategory?.displayName}' credit</b></div>
        <div><b>{priceToString(priceCategory?.singleClassPrice)}</b></div>
      </div>
    </button>}

    {bookingHasTemplateId && <button className={'BookingOverlaySelectable'} onClick={() => {
      props.onSelected({
        buySingleCredit: {
          class: props.selectedBooking.class ? findClassTemplate(props.business, props.selectedBooking.class.classTemplateId) : null,
          service: props.selectedBooking.appointment?.service ? findService(props.business, props.selectedBooking.appointment?.service.sku) : null
        }
      })
    }}>
      <div className={'BookingOverlayButtonContent'}>
        <div className={'Flex1'}><b>Single '{classOrServiceName}' credit</b></div>
        <div><b>{priceToString(paymentPolicy?.prepaid?.price)}</b></div>
      </div>
    </button>}


    {/* buy a new class pack */}
    {bookingHasTemplateId && props.business?.pricing?.packages?.map((classPack) => {

      if ((classPack.includedClassOrServiceIds?.indexOf(classOrServiceId as string) ?? -1) >= 0 ?? false) {
        return <button key={'cp' + classPack.id} className={'BookingOverlaySelectable'} onClick={() => {
          props.onSelected({buyNewClassPackage: classPack});
        }}>
          <div className={'BookingOverlayButtonContent'}>
            <div className='VerticalFlexBox Flex1'>
              <div><b>{classPack.displayName}</b></div>
              <div>Good for {classPack.classLimit == 0 ? 'unlimited' : classPack.classLimit} classes.&nbsp;
                {classPack.expirationDays == 0 ? 'No expiration.' : 'Expires in ' + classPack.expirationDays + ' days.'}
              </div>
            </div>
            <div><b>{priceToString(classPack.price)}</b></div>
          </div>

        </button>
      }
    })}

  </div>
}